import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalMessages from "@faire/web-api--source/routes/www/brand-portal/messages";
import brandPortalOrders from "@faire/web-api--source/routes/www/brand-portal/orders";
import React, { type JSX } from "react";

import { Bold } from "../../common/Bold";
import { LiItem } from "../../common/LiItem";

export const SWFProcessing: React.FC = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you need to make changes to your order or to the shipping information after you have already created your labels using Ship with Faire, the best option is to cancel the initial shipping label and create a new one."
          description={{
            text: "Description for the instant answer about updating shipping information",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To cancel the label:"
          description={{
            text: "Description for the instant answer about updating shipping information",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to the <BoldFormatting>Shipping info</BoldFormatting> section of your <OrderLinkFormatting>order details page</OrderLinkFormatting>."
              description={{
                text: "The first step content in the update tracking info",
              }}
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
                OrderLinkFormatting: (chunks: JSX.Element) =>
                  OrderLinkFormatting(chunks),
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Cancel</BoldFormatting>."
              description={{
                text: "The second step content in the update tracking info",
              }}
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Create and print new labels through <BoldFormatting>Ship with Faire</BoldFormatting>. To avoid mistakes, we suggest checking quantities and shipping details before creating your label."
              description={{
                text: "The third step content in the update tracking info",
              }}
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="We also recommend letting your retailer know about the changes to shipping details through <ButtonFormatting>Faire Messenger</ButtonFormatting> to avoid confusion."
          description={{
            text: "The fourth step content in the update tracking info",
          }}
          values={{
            ButtonFormatting: (chunks: JSX.Element) =>
              FaireMessagingFormatting(chunks),
          }}
        />
      </Typography>
    </Column>
  );
};

const FaireMessagingFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalMessages()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;

const OrderLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);
